import { useState } from "react";
import { motion } from "framer-motion";
import { Calculator } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const BRAND_GOLD = "#D4A843";
const TABBY_LOGO = "/manus-storage/tabby-logo-transparent_c799f0ee.webp";
const TAMARA_LOGO = "/manus-storage/tamara-logo-en_99665589.webp";

const VAT_RATE = 0.05; // 5% VAT

const presets = [
  { en: "Hollywood Smile (3D)", ar: "هوليوود سمايل (3D)", price: 3000 },
  { en: "Hollywood Smile (6D)", ar: "هوليوود سمايل (6D)", price: 6000 },
  { en: "Botox (Full Face)", ar: "بوتكس (الوجه كامل)", price: 1500 },
  { en: "Dental Implants", ar: "زراعة الأسنان", price: 3500 },
  { en: "Laser Package (6 sessions)", ar: "باقة ليزر (6 جلسات)", price: 2400 },
  { en: "Weight Loss Package", ar: "باقة إنقاص الوزن", price: 4000 },
];

export function InstallmentCalculator() {
  const { t, lang } = useLanguage();
  const [amount, setAmount] = useState(3000);
  const [selectedPreset, setSelectedPreset] = useState(0);

  const vatAmount = Math.ceil(amount * VAT_RATE);
  const totalWithVat = amount + vatAmount;
  const installment = Math.ceil(totalWithVat / 4);

  return (
    <section className="py-16 md:py-24 bg-gray-50">
      <div className="container max-w-4xl">
        <div className="text-center mb-12">
          <p className="text-xs tracking-[0.35em] uppercase mb-3" style={{ color: BRAND_GOLD }}>
            {t("EASY PAYMENTS", "دفع سهل")}
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-light text-gray-900 mb-3">
            {t("Pay in 4 ", "ادفع على 4 ")}<span className="italic">{t("Interest-Free", "بدون فوائد")}</span>{t(" Installments", " دفعات")}
          </h2>
          <p className="text-gray-500 text-sm max-w-md mx-auto">
            {t("Split your treatment cost into 4 easy payments with Tabby or Tamara. No interest, no hidden fees.", "قسّط تكلفة علاجك على 4 دفعات سهلة عبر تابي أو تمارا. بدون فوائد، بدون رسوم خفية.")}
          </p>
        </div>

        <div className="bg-white shadow-lg p-6 md:p-10">
          {/* Presets */}
          <div className="flex flex-wrap gap-2 mb-8 justify-center">
            {presets.map((p, i) => (
              <button
                key={i}
                onClick={() => { setSelectedPreset(i); setAmount(p.price); }}
                className={`px-4 py-2 text-xs tracking-wider uppercase border transition-all ${
                  selectedPreset === i
                    ? "border-[#D4A843] bg-[#D4A843]/10 text-gray-900 font-semibold"
                    : "border-gray-200 text-gray-500 hover:border-gray-300"
                }`}
              >
                {lang === "en" ? p.en : p.ar}
              </button>
            ))}
          </div>

          {/* Slider */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-3">
              <span className="text-xs text-gray-500 tracking-wider uppercase">{t("Treatment Cost", "تكلفة العلاج")}</span>
              <span className="text-2xl font-serif font-light text-gray-900">{amount.toLocaleString()} <span className="text-sm text-gray-400">AED</span></span>
            </div>
            <input
              type="range"
              min={500}
              max={100000}
              step={100}
              value={amount}
              onChange={(e) => { setAmount(Number(e.target.value)); setSelectedPreset(-1); }}
              aria-label="Treatment cost slider"
              className="w-full h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#D4A843]"
            />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1">
              <span>500 AED</span>
              <span>100,000 AED</span>
            </div>
          </div>

          {/* VAT Breakdown */}
          <motion.div
            key={amount}
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gray-50 p-6 md:p-8 text-center border border-gray-100"
          >
            {/* Cost breakdown with VAT */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mb-6 text-sm">
              <div className="flex flex-col items-center">
                <span className="text-[10px] text-gray-400 tracking-wider uppercase mb-1">{t("Treatment", "العلاج")}</span>
                <span className="text-gray-700 font-medium">{amount.toLocaleString()} AED</span>
              </div>
              <span className="text-gray-300 text-lg">+</span>
              <div className="flex flex-col items-center">
                <span className="text-[10px] text-gray-400 tracking-wider uppercase mb-1">{t("VAT (5%)", "ض.ق.م (5%)")}</span>
                <span className="text-gray-700 font-medium">{vatAmount.toLocaleString()} AED</span>
              </div>
              <span className="text-gray-300 text-lg">=</span>
              <div className="flex flex-col items-center">
                <span className="text-[10px] text-gray-400 tracking-wider uppercase mb-1">{t("Total", "الإجمالي")}</span>
                <span className="text-gray-900 font-semibold">{totalWithVat.toLocaleString()} AED</span>
              </div>
            </div>

            <div className="w-16 h-px bg-gray-200 mx-auto mb-6" />

            <p className="text-xs text-gray-500 tracking-wider uppercase mb-2">{t("You Pay Only", "تدفع فقط")}</p>
            <div className="flex items-baseline justify-center gap-1 mb-1">
              <span className="font-serif text-5xl md:text-6xl font-light" style={{ color: BRAND_GOLD }}>{installment.toLocaleString()}</span>
              <span className="text-gray-400 text-sm">AED</span>
            </div>
            <p className="text-gray-500 text-sm mb-4">{t("per month for 4 months (incl. VAT)", "شهرياً لمدة 4 أشهر (شامل الضريبة)")}</p>

            {/* Visual breakdown */}
            <div className="flex items-center justify-center gap-2 mb-6">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="flex flex-col items-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 border-2 flex items-center justify-center text-xs font-bold" style={{ borderColor: BRAND_GOLD, color: BRAND_GOLD }}>
                    {installment.toLocaleString()}
                  </div>
                  <span className="text-[9px] text-gray-400 mt-1">{t(`Month ${n}`, `شهر ${n}`)}</span>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-center gap-4">
              <img src={TABBY_LOGO} alt="Tabby" width="60" height="24" style={{ maxWidth: '50px', maxHeight: '20px', height: '20px' }} className="w-auto" />
              <span className="text-gray-300">|</span>
              <img src={TAMARA_LOGO} alt="Tamara" width="60" height="24" style={{ maxWidth: '50px', maxHeight: '20px', height: '20px' }} className="w-auto" />
            </div>
          </motion.div>

          <p className="text-center text-[10px] text-gray-400 mt-4">
            {t("0% interest • 5% VAT included • No hidden fees • Instant approval • Available for all treatments", "0% فوائد • شامل 5% ضريبة القيمة المضافة • بدون رسوم خفية • موافقة فورية • متاح لجميع العلاجات")}
          </p>
        </div>
      </div>
    </section>
  );
}
